import { apiService } from './api';
import { User } from '../contexts/AuthContext';

interface LoginResponse {
  success: boolean;
  token?: string;
  user?: User;
  requires2FA?: boolean;
  tempToken?: string;
  message?: string;
}

interface UserResponse {
  success: boolean;
  user: User;
}

class AuthService {
  async loginAdmin(email: string, password: string): Promise<{ user?: User; token?: string; requires2FA?: boolean; tempToken?: string }> {
    const response = await apiService.post<LoginResponse>('/auth/login-admin', {
      email,
      password,
    });

    if (!response.success) {
      throw new Error('Login failed');
    }

    // Check if 2FA is required
    if (response.requires2FA && response.tempToken) {
      return {
        requires2FA: true,
        tempToken: response.tempToken,
      };
    }

    return {
      user: response.user,
      token: response.token,
    };
  }

  async loginPublisher(email: string, password: string): Promise<{ user?: User; token?: string; requires2FA?: boolean; tempToken?: string }> {
    const response = await apiService.post<LoginResponse>('/auth/login-publisher', {
      email,
      password,
    });

    if (!response.success) {
      throw new Error('Login failed');
    }

    // Check if 2FA is required
    if (response.requires2FA && response.tempToken) {
      return {
        requires2FA: true,
        tempToken: response.tempToken,
      };
    }

    return {
      user: response.user,
      token: response.token,
    };
  }

  async loginClient(email: string, password: string): Promise<{ user?: User; token?: string; requires2FA?: boolean; tempToken?: string }> {
    const response = await apiService.post<LoginResponse>('/auth/login-client', {
      email,
      password,
    });

    if (!response.success) {
      throw new Error('Login failed');
    }

    if (response.requires2FA && response.tempToken) {
      return {
        requires2FA: true,
        tempToken: response.tempToken,
      };
    }

    return {
      user: response.user,
      token: response.token,
    };
  }

  async loginAgency(email: string, password: string): Promise<{ user?: User; token?: string; requires2FA?: boolean; tempToken?: string }> {
    const response = await apiService.post<LoginResponse>('/auth/login-agency', {
      email,
      password,
    });

    if (!response.success) {
      throw new Error('Login failed');
    }

    if (response.requires2FA && response.tempToken) {
      return {
        requires2FA: true,
        tempToken: response.tempToken,
      };
    }

    return {
      user: response.user,
      token: response.token,
    };
  }

  async loginPortal(email: string, password: string): Promise<{ user?: User; token?: string; requires2FA?: boolean; tempToken?: string }> {
    const response = await apiService.post<LoginResponse>('/auth/login-portal', {
      email,
      password,
    });

    if (!response.success) {
      throw new Error('Login failed');
    }

    if (response.requires2FA && response.tempToken) {
      return {
        requires2FA: true,
        tempToken: response.tempToken,
      };
    }

    return {
      user: response.user,
      token: response.token,
    };
  }

  async login(email: string, password: string): Promise<{ user?: User; token?: string; requires2FA?: boolean; tempToken?: string }> {
    const response = await apiService.post<LoginResponse>('/auth/login', {
      email,
      password,
    });

    if (!response.success) {
      throw new Error('Login failed');
    }

    // Check if 2FA is required
    if (response.requires2FA && response.tempToken) {
      return {
        requires2FA: true,
        tempToken: response.tempToken,
      };
    }

    return {
      user: response.user,
      token: response.token,
    };
  }

  async verify2FA(tempToken: string, code: string): Promise<{ user: User; token: string }> {
    const response = await apiService.post<LoginResponse>('/2fa/verify-login', {
      tempToken,
      code,
    });

    if (!response.success || !response.user || !response.token) {
      throw new Error('2FA verification failed');
    }

    return {
      user: response.user,
      token: response.token,
    };
  }

  async getCurrentUser(): Promise<User> {
    const response = await apiService.get<UserResponse>('/auth/me');
    
    if (!response.success) {
      throw new Error('Failed to get user data');
    }

    return response.user;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await apiService.put('/auth/change-password', {
      currentPassword,
      newPassword,
    });
  }

  async verifyPassword(password: string): Promise<void> {
    const response = await apiService.post<{ success: boolean; message?: string }>('/auth/verify-password', {
      password,
    });

    if (!response.success) {
      throw new Error('Password verification failed');
    }
  }

  async verifySensitivePagePassword(password: string): Promise<void> {
    const response = await apiService.post<{ success: boolean; message?: string }>('/auth/verify-sensitive-password', {
      password,
    });

    if (!response.success) {
      throw new Error('Sensitive page password verification failed');
    }
  }

  async updateSensitivePagePassword(loginPassword: string, newSensitivePassword: string): Promise<void> {
    const response = await apiService.put<{ success: boolean; message?: string }>('/auth/sensitive-page-password', {
      loginPassword,
      newSensitivePassword,
    });

    if (!response.success) {
      throw new Error('Failed to update sensitive page password');
    }
  }
}

export const authService = new AuthService();
